import './AppSidebar.scss';

import React, { useCallback, useState } from 'react'
import { Offcanvas, Nav, Collapse } from 'react-bootstrap'
import { Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import classNames from 'classnames';
import { useEffect } from 'react';
import { EventEmitter } from '../../utils/EventEmitter';
import { useAuth } from '../../contexts/auth';
import { IconDefinition, SizeProp } from '@fortawesome/fontawesome-svg-core';
import { SectionLabels } from '../../api/ControlPanelManager';
import { isAdmin, mapSections } from '../../utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

export type SectionKey =
  'dashboard' | 'data-management' | 'assets-management' | 'llm' | 'control-panel' |
  'datasources' | 'datasets' | 'models' | 'services' | 'applications' | 'projects' |
  'rags' | 'loaders' | 'llm-models' | 'api-keys' | 'environment-variables' | 'dataspace-connectors' | 'persona-analyzer' | 'spiderisk' | 'enhance';
export type NavigationSection = {
  labels: SectionLabels;
  order?: number;
  url?: string;
  hide?: boolean;
  admin?: boolean;
  subSection?: boolean;
  items?: SectionKey[];
  external?: boolean;
}
export type NavigationLink = Pick<NavigationSection, 'url' | 'hide' | 'admin' | 'order'> & {
  key: string;
  icon: IconDefinition;
  title: string;
  items?: NavigationLink[];
  external?: boolean;
}

type LinkProps = {
  link: NavigationLink;
  vertical?: boolean;
  iconSize?: SizeProp;
}

export function SidebarLink({ link, iconSize, vertical }: LinkProps) {
  const location = useLocation();
  const match = location.pathname.startsWith(link.url!);

  return (
    <Link className={classNames("sidebar-link", { active: match })} to={link.url!} target={link.external ? "_blank" : "_self"}>
      <FontAwesomeIcon icon={link.icon || solid('circle-chevron-right')} fixedWidth={true} size={iconSize} />
      <span className={classNames({ "ms-3": !vertical })}>{link.title}</span>
    </Link>
  );
}

export function SidebarGroup({ link, iconSize, vertical }: LinkProps) {
  const location = useLocation();
  const childMatch = link.items!.some(child => location.pathname.startsWith(child.url!));
  const [open, setOpen] = useState(childMatch);

  const toggleGroup = useCallback(() => setOpen(v => !v), []);

  return (
    <div className="sidebar-group">
      <div className={classNames("sidebar-link", { active: childMatch })} onClick={toggleGroup}>
        <FontAwesomeIcon icon={link.icon} fixedWidth={true} size={iconSize} />
        <span className={classNames({ "ms-3": !vertical })}>{link.title}</span>
        <FontAwesomeIcon icon={open ? solid('chevron-down') : solid('chevron-right')} className="ms-auto" fixedWidth />
      </div>

      <Collapse in={open}>
        <div>
          <div className="sidebar-group-items">
            {link.items!.map(item => <SidebarLink key={item.key} link={item} />)}
          </div>
        </div>
      </Collapse>
    </div>
  );
}

type Props = {
  show: boolean;
  handleClose: VoidFunction;
}

function AppSidebar({ show, handleClose }: Props) {
  const [timestamp, setTimestamp] = useState(new Date().getTime());
  const location = useLocation();
  const { loggedUser } = useAuth();

  const navigationSections = useSelector((state: RootState) => state.sectionStore.sections);
  const navigationLinks: NavigationLink[] = Object.entries(navigationSections)
    .filter(([_key, section]) => !section.subSection)
    .map(entry => mapSections(entry, navigationSections))
    .sort((a, b) => a.order && b.order ? a.order - b.order : 0);

  // eslint-disable-next-line
  useEffect(() => handleClose(), [location.pathname]);

  useEffect(() => {
    EventEmitter.subscribe('update-timestamp', (ts: number) => setTimestamp(ts));
  }, []);

  return (
    <Offcanvas show={show} onHide={handleClose}>
      <div className="alida-sidebar" style={{ backgroundImage: `url(${window.__env.SIDEBAR_BG_IMAGE}?t=${timestamp})` }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {/* some optional title here */}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="sidebar-content">
          <Nav defaultActiveKey="/" className="flex-column">
            {navigationLinks
              .filter(link => !link.hide)
              .filter(link => link.key !== 'dataspace-connectors' || window.__env.DATASPACES_ENABLED)
              .filter(link => link.key !== 'llm' || window.__env.RAGGER_ENABLED)
              .filter(link => !link.admin || isAdmin(loggedUser))
              .map(link => link.items ? <SidebarGroup link={link} key={link.key} /> : <SidebarLink key={link.key} link={link} />)}
            {window.__env.JUPYTER_UI_URL && window.__env.JUPYTER_ENABLED && (
              <a href={window.__env.JUPYTER_UI_URL} target="_blank" rel="noopener noreferrer" className="sidebar-link">
                <FontAwesomeIcon icon={solid('book-bookmark')} fixedWidth />
                <span className="ms-3">Notebooks</span>
              </a>
            )}
            {window.__env.DOCUMENTATION_URL && window.__env.DOCUMENTATION_ENABLED && (
              <a href={window.__env.DOCUMENTATION_URL} target="_blank" rel="noopener noreferrer" className="sidebar-link">
                <FontAwesomeIcon icon={solid('question-circle')} fixedWidth />
                <span className="ms-3">Documentation</span>
              </a>
            )}
          </Nav>
        </Offcanvas.Body>
      </div>
    </Offcanvas>
  )
}

export default AppSidebar
